import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import intl from "../../i18n/intl";
import { errorMessageId } from "../../helpers/UserUploadHelper";
import { resetAll } from "./baseSlice";

const customIntl = intl();

// Errors are { status, code, message } (UserUploadSaga errorOf). A 401 signs
// the user out (sessionMiddleware), so it needs no message.
const showError = (error) => {
  if (error?.status !== 401) {
    toast.error(customIntl.formatMessage({ id: errorMessageId(error?.code) }));
  }
};

const initialState = {
  list: [],
  totalCount: 0,
  query: { pageNo: 1, pageSize: 20 },
  // The upload the dialog started and is polling.
  current: null,
  // The upload opened from the history page.
  record: null,
  requesting: false,
  action: null,
  error: null,
};

const userUploadSlice = createSlice({
  name: "userUploads",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    // Upload and poll errors are shown inside the dialog, not as toasts.
    userUploadRequest: (state) => {
      state.requesting = true;
      state.action = "upload";
      state.error = null;
      state.current = null;
    },
    userUploadSuccess: (state, action) => {
      state.requesting = false;
      state.current = action.payload?.response || null;
    },
    userUploadFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
    },
    userUploadPollRequest: (state) => {
      state.action = "poll";
      state.error = null;
    },
    userUploadPollSuccess: (state, action) => {
      const upload = action.payload?.response;
      if (upload && state.current?.id === upload.id) {
        state.current = upload;
      }
    },
    userUploadPollFail: (state, action) => {
      state.error = action.payload;
    },
    userUploadReset: (state) => {
      state.current = null;
      state.requesting = false;
      state.action = null;
      state.error = null;
    },

    userUploadListRequest: (state) => {
      state.requesting = true;
      state.action = "list";
      state.error = null;
    },
    userUploadListSuccess: (state, action) => {
      state.requesting = false;
      state.list = action.payload?.response?.list || [];
      state.totalCount = action.payload?.response?.totalCount || 0;
    },
    userUploadListFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      state.list = [];
      state.totalCount = 0;
      showError(action.payload);
    },
    userUploadSetQuery: (state, action) => {
      state.query = { ...state.query, ...action.payload };
    },

    userUploadDetailsRequest: (state) => {
      state.requesting = true;
      state.action = "details";
      state.error = null;
      state.record = null;
    },
    userUploadDetailsSuccess: (state, action) => {
      state.requesting = false;
      state.record = action.payload?.response || null;
    },
    userUploadDetailsFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      showError(action.payload);
    },

    userUploadDownloadRequest: () => {},
    userUploadDownloadSuccess: () => {},
    userUploadDownloadFail: (state, action) => {
      showError(action.payload);
    },
  },
});

export const {
  userUploadRequest,
  userUploadSuccess,
  userUploadFail,
  userUploadPollRequest,
  userUploadPollSuccess,
  userUploadPollFail,
  userUploadReset,
  userUploadListRequest,
  userUploadListSuccess,
  userUploadListFail,
  userUploadSetQuery,
  userUploadDetailsRequest,
  userUploadDetailsSuccess,
  userUploadDetailsFail,
  userUploadDownloadRequest,
  userUploadDownloadSuccess,
  userUploadDownloadFail,
} = userUploadSlice.actions;
export default userUploadSlice.reducer;
